/**
 * Call sounds: ringtone for incoming calls, ringback for outgoing ones.
 *
 * Browsers only let a page start audio after the user has interacted with it, and on iPhone Web Audio is
 * silenced by the mute switch. So the sounds are real <audio> elements that get "unlocked" on the first
 * touch/click anywhere in the app (a muted play + pause inside the gesture). After that an incoming call
 * can start the ringtone on its own while the app is open. When the app is closed or the phone is locked,
 * the push notification's sound is what rings (pages cannot play audio then).
 */
type Tone = 'incoming' | 'outgoing';

const players: Partial<Record<Tone, HTMLAudioElement>> = {};
let vibrateTimer: ReturnType<typeof setInterval> | undefined;
let unlocked = false;

function player(kind: Tone) {
  let a = players[kind];
  if (!a) {
    a = new Audio(kind === 'incoming' ? '/sounds/ringtone.wav' : '/sounds/ringback.wav');
    a.loop = true;
    a.preload = 'auto';
    players[kind] = a;
  }
  return a;
}

function unlock() {
  if (unlocked) return;
  unlocked = true;
  for (const kind of ['incoming', 'outgoing'] as const) {
    const a = player(kind);
    if (!a.paused) continue; // already ringing
    a.muted = true;
    a.play()
      .then(() => {
        if (a.muted) {
          a.pause();
          a.currentTime = 0;
        }
        a.muted = false;
      })
      .catch(() => {
        a.muted = false;
        unlocked = false; // try again on the next gesture
      });
  }
}

/** Call once at startup: unlocks audio on the first user gesture. */
export function primeSounds() {
  const opts = { capture: true, passive: true } as const;
  const handler = () => {
    unlock();
    if (unlocked) {
      window.removeEventListener('pointerdown', handler, opts);
      window.removeEventListener('keydown', handler, opts);
    }
  };
  window.addEventListener('pointerdown', handler, opts);
  window.addEventListener('keydown', handler, opts);
}

export function startTone(kind: Tone) {
  stopTone();
  const a = player(kind);
  a.muted = false;
  a.currentTime = 0;
  void a.play().catch(() => {
    /* not unlocked yet: the call screen is still shown, the push notification rings when in background */
  });
  if (kind === 'incoming' && navigator.vibrate) {
    const buzz = () => navigator.vibrate?.([700, 400, 700]);
    buzz();
    vibrateTimer = setInterval(buzz, 3000);
  }
}

export function stopTone() {
  for (const a of Object.values(players)) {
    if (a && !a.paused && !a.muted) {
      a.pause();
      a.currentTime = 0;
    }
  }
  clearInterval(vibrateTimer);
  vibrateTimer = undefined;
  navigator.vibrate?.(0);
}
